import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { App } from '@df/core/app';
import { Subscription } from 'rxjs';
import { ICmsShortcutsProvider, IShortcutChildItem, IShortcutItem, TActionResult } from './icms-shortcuts.provider';
import { ICmsShortcutsSearchComponent } from './search/icms-shortcuts-search.component';
import { IcmsIcons } from '@icms/ui/icons/generated/icms-icons';

@Component({
    standalone: false,
    selector: 'icms-shortcuts',
    templateUrl: './icms-shortcuts.html',
    styleUrls: ['./icms-shortcuts.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ICmsShortcutsComponent implements OnInit, OnDestroy {
    /**
     * Search quere entered by user
     */
    protected query: string = '';

    /**
     * Component mode
     */
    protected mode: 'search' | 'default' = 'default';

    /**
     * Displayed actions list
     */
    protected results: IShortcutItem[] = [];

    /**
     * Input element
     */
    @ViewChild('input', { static: true, read: ElementRef })
    protected inputElement?: ElementRef<HTMLInputElement>;

    /**
     * Focused item
     */
    protected selected?: IShortcutChildItem;

    /**
     * Icons
     */
    protected icons = IcmsIcons;

    /**
     * Subscriptions
     */
    private childStatusSubscriptions: Subscription[] = [];

    /**
     * All commands
     */
    private allCommands: IShortcutItem[] = [];

    /**
     * Currently considered commands
     */
    private commands: IShortcutItem[] = [];

    /**
     * Entry path
     */
    private path: IShortcutItem[][] = [];

    /**
     * Search component
     */
    @ViewChild(ICmsShortcutsSearchComponent, { static: false })
    protected searchComponent?: ICmsShortcutsSearchComponent;

    /**
     * Services
     */

    constructor(
        private app: App,
        private changeDetectorRef: ChangeDetectorRef,
        private iCmsShortcutsProvider: ICmsShortcutsProvider,
        private elementRef: ElementRef<HTMLElement>
    ) {}

    ngOnInit() {
        this.changeDetectorRef.detach();
        this.allCommands = this.iCmsShortcutsProvider.getValue(this.app.meta.model);
        this.allCommands.forEach(item => {
            item.children.forEach(child => {
                if (child.status) {
                    this.childStatusSubscriptions.push(child.status.subscribe(() => this.detectChanges()));
                }
            });
        });
        this.setCommands('reset');
    }

    ngOnDestroy(): void {
        this.clearChildSubscriptions();
    }

    private setCommands(commands: TActionResult) {
        if (commands) {
            switch (commands) {
                case 'back':
                    this.commands = this.path.pop() || this.allCommands;
                    break;
                case 'reset':
                    this.commands = this.allCommands;
                    this.path = [];
                    break;
                default:
                    this.path.push(this.commands);
                    this.commands = commands;
            }
            this.query = '';
            this.onSearchChange();
            this.inputElement?.nativeElement.focus();
        }
    }

    protected onSearchChange() {
        const query = this.query?.trim().toLowerCase();
        switch (query?.[0]) {
            case '/':
                this.mode = 'search';
                break;
            default:
                this.mode = 'default';
                this.results = query
                    ? (this.commands
                          .map(item => {
                              if (item.title.toLowerCase().includes(query)) {
                                  return item;
                              }
                              const children = item.children.filter(i => i.command.toLowerCase().includes(query));
                              if (children.length) {
                                  return {
                                      ...item,
                                      children
                                  };
                              }
                              return false;
                          })
                          .filter(i => !!i) as IShortcutItem[])
                    : this.commands;
                this.selected = this.results[0]?.children?.[0];
                break;
        }
        this.detectChanges();
    }

    protected select(item: IShortcutChildItem) {
        if (this.selected !== item) {
            this.selected = item;
            this.detectChanges();
        }
    }

    protected isSelected(item: IShortcutChildItem): boolean {
        return this.selected === item;
    }

    protected apply(item: IShortcutChildItem | undefined = this.selected, event?: Event) {
        if (event) {
            event.preventOffClick = true;
        }
        if (this.mode === 'search') {
            this.searchComponent?.apply();
        } else if (item) {
            this.setCommands(item.action());
        }
    }

    protected onKey(event: KeyboardEvent) {
        switch (event.key) {
            case 'ArrowDown':
                if (this.mode === 'search') {
                    this.searchComponent?.shiftSelection(1);
                } else {
                    this.shiftSelection(1);
                    event.preventDefault();
                }
                break;
            case 'ArrowUp':
                if (this.mode === 'search') {
                    this.searchComponent?.shiftSelection(-1);
                } else {
                    this.shiftSelection(-1);
                    event.preventDefault();
                }
                break;
            case 'Enter':
                this.apply();
                event.preventDefault();
                break;
        }
    }

    private shiftSelection(shift: 1 | -1) {
        const selected = this.selected || this.results[0]?.children[0];
        if (!selected) {
            return;
        }
        const index = this.results.findIndex(i => i.children.includes(selected));
        const selectedChildIndex = this.results[index].children.indexOf(selected);
        const targetChild = this.results[index].children[selectedChildIndex + shift];
        if (targetChild) {
            this.selected = targetChild;
        } else {
            const target = this.results[index + shift];
            const targetChild2 = shift > 0 ? target?.children[0] : target?.children[target.children.length - 1];
            if (targetChild2) {
                this.selected = targetChild2;
            } else {
                if (shift > 0) {
                    this.selected = this.results[0]?.children[0];
                } else {
                    const last = this.results[this.results.length - 1];
                    this.selected = last?.children[last.children.length - 1];
                }
            }
        }
        this.detectChanges();
        this.elementRef.nativeElement.querySelector('._selected')?.scrollIntoView();
    }

    private detectChanges() {
        this.changeDetectorRef.detectChanges();
    }

    private clearChildSubscriptions() {
        if (this.childStatusSubscriptions) {
            this.childStatusSubscriptions.forEach(s => s.unsubscribe());
            this.childStatusSubscriptions = [];
        }
    }
}
